<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-layout row wrap justify-space-between>
        <dt-search-box
          :search="search"
          @typing="searchValue"
          @reset-datatable="resetDataTable()"
          @clear-search-field="search = null"
        />
        <v-layout row wrap justify-end class="pt-2 pb-2">
          <v-btn
            dark
            color="primary"
            v-if="hasPermissions(['remittance_offices_store'])"
            @click="dialogs.addDialog = true"
          >
            <v-icon>add</v-icon>
            {{ $t('datatable-add-new') }}
          </v-btn>
        </v-layout>
      </v-layout>
    </v-card>

    <v-card flat>
      <v-layout row wrap justify-end>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 0px; height: 30px; width: 30px"
              :loading="loadThePrintBtn"
              flat
              fab
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('print')"
            >
              <v-icon>print</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Print Table`) }}</span>
        </v-tooltip>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 20px 5px 10px; height: 30px; width: 30px"
              fab
              :loading="loadTheExportBtn"
              flat
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('export')"
            >
              <v-icon>fa-file-excel</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Export Table`) }}</span>
        </v-tooltip>
      </v-layout>
    </v-card>

    <v-card class="px-4 pb-4">
      <v-data-table
        v-show="isDatatable"
        :headers="headers"
        :items="resultData.data"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="pagination.rowsPerPageItems"
        :total-items="resultData.total"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.remittance_office_id }}</td>-->
          <td>{{ props.item.remittance_office_name }}</td>
          <td>{{ props.item.remittance_office_phone }}</td>
          <td>{{ props.item.remittance_office_mail }}</td>
          <td>{{ props.item.remittance_office_address }}</td>
          <td>{{ returnCountryName(props.item.remittance_office_country) }}</td>
          <td>{{ props.item.remittance_office_governorate }}</td>
          <td>{{ props.item.remittance_office_city }}</td>
          <td v-if="hasActionsMenuPermissions()">
            <v-menu offset-y bottom :left="!$vuetify.rtl" :right="$vuetify.rtl" transition="slide-y-reverse-transition">
              <v-btn flat fab icon class="transparent py-2" slot="activator">
                <v-icon large class="grey--text actions-btn">more_vert</v-icon>
              </v-btn>
              <v-list>
                <v-tooltip
                  v-if="hasPermissions(['remittance_offices_show'])"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="viewRecord(props.item.remittance_office_id)">
                    <v-icon>chrome_reader_mode</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-view') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`View Remittance Office information`) }}</span>
                </v-tooltip>

                <v-tooltip
                  v-if="hasPermissions(['remittance_offices_update'])"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="editRecord(props.item.remittance_office_id)">
                    <v-icon>edit</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-edit') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`Edit Remittance Office information`) }}</span>
                </v-tooltip>

                <v-tooltip
                  v-if="hasPermissions(['remittance_transactions_show'])"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="transactions(props.item)">
                    <v-icon>swap_horiz</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('Transactions') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`View Remittance Transactions Information`) }}</span>
                </v-tooltip>

                <v-divider v-if="hasPermissions(['remittance_offices_destroy'])" />

                <v-tooltip
                  v-if="hasPermissions(['remittance_offices_destroy'])"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="showConfirmDeletion(props.item.remittance_office_id)">
                    <v-icon class="red--text">delete</v-icon>
                    <v-list-tile-content class="red--text px-3">{{ $t('datatable-delete') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t('Delete Remittance Office Record') }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
          $t('Sorry, No results found for your search criteria')
        }}</v-alert>
      </v-data-table>

      <dt-pagination
        :resultData="resultData"
        :pagination="pagination"
        :perPageNumber="perPageNumber"
        @page-changed="pageChanged"
        @per-page-changed="resultsPerPageChanged"
      />
    </v-card>

    <add-remittance
      :addDialog="dialogs.addDialog"
      @reset-datatable="resetDataTable(), resetAllErrors(true)"
      @hidemodal="resetAllErrors(true), (dialogs.addDialog = false)"
    />

    <edit-remittance
      :editDialog="dialogs.editDialog"
      @updated="updateDatatableResult(), (dialogs.editDialog = false), (allowGettingData = false)"
      @hidemodal="resetAllErrors(true), (dialogs.editDialog = false)"
    />

    <confirm-deletion
      :deleteDialog="dialogs.deleteDialog"
      :btnDeleteLoading="btnDeleteLoading"
      @hidemodal="dialogs.deleteDialog = false"
      @confirm="deleteAction()"
    />

    <view-remittance :viewDialog="dialogs.viewDialog" @hidemodal="dialogs.viewDialog = false" />

    <print-export-component
      :printExportDialog="dialogs.printExportDialog"
      :tableHeader="tableHeader"
      :tableBody="tableBody"
      :clickedButton="clickedButton"
      @hidemodal="dialogs.printExportDialog = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import Axios from 'axios'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    dtPagination: () => import('@/views/_partials/AppDatatablePagination.vue'),
    addRemittance: () => import('./addRemittance.vue'),
    editRemittance: () => import('./editRemittance.vue'),
    viewRemittance: () => import('./viewRemittance.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue')
  },

  data() {
    return {
      dialogs: {
        viewDialog: false,
        addDialog: false,
        editDialog: false,
        deleteDialog: false,
        printExportDialog: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      deleteId: 0,
      btnDeleteLoading: false,
      isDatatable: true,
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'remittance_office_id',
      perPageNumber: 10,
      pagination: {
        sortBy: this.defaultSortBy,
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 1
      },
      oldPagination: {},
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'remittance_office_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Name'), value: 'remittance_office_name' },
        { text: this.$t('Phone'), value: 'remittance_office_phone' },
        { text: this.$t('Email'), value: 'remittance_office_mail' },
        { text: this.$t('Address'), value: 'remittance_office_address' },
        { text: this.$t('Country'), value: 'remittance_office_country' },
        {
          text: this.$t('Governorate'),
          value: 'remittance_office_governorate'
        },
        { text: this.$t('City'), value: 'remittance_office_city' }
      ],
      allowGettingData: true
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: this.$t('Remittances'),
      icon: 'attach_money'
    })

    this.setCountriesListEn()
    this.setCountriesListAr()

    if (this.hasActionsMenuPermissions()) {
      this.headers.push({
        text: this.$t('datatable-actions'),
        value: '',
        sortable: false
      })
    }
  },

  computed: {
    ...mapGetters({
      resultData: 'remittances/getRemittanceOfficeList'
    })
  },

  watch: {
    pagination: {
      immediate: true,
      deep: true,
      handler() {
        let p = this.pagination
        let op = this.oldPagination
        if (p.sortBy !== op.sortBy || p.descending !== op.descending) {
          op.sortBy = p.sortBy
          op.descending = p.descending
          p.page = 1
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        } else if (this.allowGettingData || this.search !== this.oldSearch) {
          this.oldSearch = this.search
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        }
      }
    }
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'remittances/updateRemittanceOfficeList',
      deleteRecord: 'remittances/deleteRemittanceOffice',
      fetchRemittanceOfficeInfo: 'remittances/fetchRemittanceOfficeInfo',
      setCountriesListEn: 'countries/setFullCountriesListEn',
      setCountriesListAr: 'countries/setFullCountriesListAr'
    }),

    returnCountryName(countryId) {
      return helpers.getCountryName(countryId)
    },

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get('remittance_offices_index', {
        params: { is_paginated: 0, search: this.search }
      })
        .then(response => {
          this.tableBody = response.data.data
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    hasActionsMenuPermissions() {
      return helpers.userHasPermissions([
        'remittance_offices_show',
        'remittance_offices_update',
        'remittance_offices_destroy'
      ])
    },

    updateDatatableResult() {
      this.loading = true
      let payload = { ...this.pagination, search: this.search }
      this.updateResult(payload)
        .then(() => {
          this.pagination.page = this.resultData.current_page
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    resetDataTable() {
      this.allowGettingData = true
      this.pagination = {
        sortBy: null,
        descending: true,
        page: 1,
        rowsPerPage: this.perPageNumber,
        totalItems: 1
      }
      this.search = this.oldSearch = null
    },

    resultsPerPageChanged(val) {
      this.perPageNumber = val
      this.pagination.rowsPerPage = val
      this.pagination.page = 1
      this.updateDatatableResult()
    },

    pageChanged(val) {
      this.pagination.page = val
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
    },

    viewRecord(id) {
      this.fetchRemittanceOfficeInfo(id).then(() => {
        this.dialogs.viewDialog = true
      })
    },

    editRecord(id) {
      this.fetchRemittanceOfficeInfo(id).then(() => {
        this.dialogs.editDialog = true
      })
    },

    transactions(remittance) {
      this.$router.push({
        name: 'accounting-remittance-office-transactions',
        query: {
          id: remittance.remittance_office_id,
          name: remittance.remittance_office_name
        }
      })
    },

    showConfirmDeletion(id) {
      this.deleteId = id
      this.dialogs.deleteDialog = true
    },

    deleteAction() {
      this.btnDeleteLoading = true
      this.deleteRecord(this.deleteId)
        .then(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
          let payload = { ...this.pagination, search: this.search }
          this.updateResult(payload)
        })
        .catch(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
        })
    }
  }
}
</script>

<style scoped />
